import {FC, memo, ReactNode, useCallback, useMemo, useRef, useState} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {usePreferences} from '@/components/context/Preferences'
import {MarketsContactBanner} from '@/components/form/markets/MarketsContactBanner'
import {SearchInput} from '@/components/inputs/SearchInput'
import {CountryCard, filterEuCountries, isIncludedInEu} from '@/content/countryCards'
import {getPricingContent} from '@/content/pricing'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {Jurisdiction} from '@/utils/countries/constants'
import {normalizeJurisdictionName} from '@/utils/countries/helpers'
import {Currency} from '@/utils/currency'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {MarketsOptionCard} from '../../form/markets/selection/MarketsOptionCard'

export type JurisdictionPricingInfo = {
  price: number
  renderTooltip: (defaultTooltipTextKey: string) => ReactNode
}

type CountryOptionRowProps = {
  jurisdiction: Jurisdiction
  title: string
  price: number
  tooltipNode: ReactNode
  isChecked: boolean
  isEuIncluded: boolean
  onToggle: (jurisdiction: Jurisdiction) => void
}

// Memoized so toggling one checkbox doesn't re-render every other country in the list
const CountryOptionRow: FC<CountryOptionRowProps> = memo(
  ({jurisdiction, title, price, tooltipNode, isChecked, isEuIncluded, onToggle}) => (
    <MarketsOptionCard
      jurisdiction={jurisdiction}
      title={title}
      price={price}
      isSelected={isChecked}
      euIncluded={isEuIncluded}
      onChange={() => onToggle(jurisdiction)}
      tooltipText={tooltipNode}
    />
  ),
)

type PricingCountryCheckboxListProps = {
  selectedJurisdictions: Jurisdiction[]
  setSelectedJurisdictions: (selectedJurisdictions: Jurisdiction[]) => void
  countryCardsList: {
    title: string
    list: CountryCard[]
    isSearchable?: boolean
  }[]
  getJurisdictionPricing: (
    jurisdiction: Jurisdiction,
    currency: Currency,
  ) => JurisdictionPricingInfo
  allowEuMemberSelection: boolean
}

export const PricingCountryCheckboxList: FC<PricingCountryCheckboxListProps> = ({
  selectedJurisdictions,
  setSelectedJurisdictions,
  countryCardsList,
  getJurisdictionPricing,
  allowEuMemberSelection,
}) => {
  const {t} = useTranslation('page')

  const {currency} = usePreferences()
  const {searchPlaceholder} = getPricingContent().calculator
  const [query, setQuery] = useState('')

  // Kept stable across renders (via a ref for the current selection) so it can be handed to
  // CountryOptionRow without invalidating its memoization on every render.
  const selectedJurisdictionsRef = useRef(selectedJurisdictions)
  selectedJurisdictionsRef.current = selectedJurisdictions

  const toggleJurisdiction = useCallback(
    (code: Jurisdiction) => {
      const current = selectedJurisdictionsRef.current
      setSelectedJurisdictions(
        current.includes(code)
          ? current.filter((jurisdiction) => jurisdiction !== code)
          : !allowEuMemberSelection && [...current, code].includes(Jurisdiction.EU)
          ? filterEuCountries([...current, code])
          : [...current, code],
      )
      setQuery('')
    },
    [allowEuMemberSelection, setSelectedJurisdictions],
  )

  // Price and tooltip content only depend on the jurisdiction/currency, not on which countries
  // are currently selected - memoize them so they aren't recomputed for the whole list (which
  // includes every jurisdiction, not just the recommended ones) on every checkbox click.
  const pricingByCountryCard = useMemo(() => {
    const map = new Map<string, {price: number; tooltipNode: ReactNode}>()
    countryCardsList.forEach(({list}) => {
      list.forEach(({key, tooltipText}) => {
        const cacheKey = `${key}::${tooltipText}`
        if (!map.has(cacheKey)) {
          const {price, renderTooltip} = getJurisdictionPricing(key, currency)
          map.set(cacheKey, {price, tooltipNode: renderTooltip(tooltipText)})
        }
      })
    })
    return map
  }, [countryCardsList, currency, getJurisdictionPricing])

  const normalizedQuery = normalizeJurisdictionName(query)
  const filteredCountryCardsList = countryCardsList.map(({title, list, isSearchable}) => ({
    title,
    list:
      isSearchable && query
        ? list.filter(({title}) => normalizeJurisdictionName(title).indexOf(normalizedQuery) >= 0)
        : list,
    isSearchable,
  }))

  return (
    <>
      {filteredCountryCardsList.map(({title, list, isSearchable}) => (
        <div key={title} className="wrapper">
          <div className="title">
            <Text variant={TextVariant.h5} element="h3" fontWeight={TextFontWeight.bold}>
              {title}
            </Text>
          </div>
          {isSearchable && (
            <div className="search">
              <SearchInput query={query} setQuery={setQuery} placeholder={t(searchPlaceholder)} />
            </div>
          )}
          {list.length > 0 ? (
            <ul className="list">
              {list.map(({key, title, tooltipText}) => {
                const isEuIncluded =
                  !allowEuMemberSelection && isIncludedInEu(key, selectedJurisdictions)
                const isChecked = selectedJurisdictions.includes(key)
                const {price, tooltipNode} = pricingByCountryCard.get(`${key}::${tooltipText}`)!

                return (
                  <CountryOptionRow
                    key={key}
                    jurisdiction={key}
                    title={title}
                    price={price}
                    tooltipNode={tooltipNode}
                    isChecked={isChecked}
                    isEuIncluded={isEuIncluded}
                    onToggle={toggleJurisdiction}
                  />
                )
              })}
            </ul>
          ) : (
            <MarketsContactBanner />
          )}
        </div>
      ))}

      <style jsx>{`
        .wrapper + .wrapper {
          margin-top: ${theme.spacing[6]};
        }

        .title {
          margin-bottom: ${theme.spacing[3]};
        }

        .list {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          margin: -${theme.spacing[3]} -${theme.spacing[2]} 0 -${theme.spacing[2]};
        }

        .search {
          margin-bottom: ${theme.spacing[4]};
        }

        @media ${MOBILE_SCREENSIZE} {
          .list {
            margin-left: 0;
            margin-right: 0;
          }
        }
      `}</style>
    </>
  )
}
